import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import logoAsset from "@/assets/mowzi-logo.png.asset.json";
import stripesAsset from "@/assets/mowzi-stripes.png.asset.json";
const logo = logoAsset.url;
const stripes = stripesAsset.url;

type Mode = "login" | "signup" | "forgot";

const emailSchema = z.string().trim().email("Enter a valid email").max(255);
const passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(72);
const phoneSchema = z.string().trim().min(7, "Enter a valid mobile number").max(30);

const codeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{4}$/, "Company code must be exactly 4 letters");

const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: z.string().trim().min(1, "Name is required").max(100),
  phone: z.string().trim().min(7, "Enter a valid phone number").max(30),
  company: z.string().trim().max(120).optional(),
  location: z.string().trim().min(2, "Where are you based?").max(120),
  role: z.enum(["owner", "mower"]),
  companyCode: codeSchema,
});


const perks = [
  "Instant satellite lawn quotes",
  "Weekend scheduling that fills itself",
  "Live crew tracking on every job",
  "Payments collected automatically",
];

export default function AuthPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [mode, setMode] = useState<Mode>((params.get("mode") as Mode) || "login");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [role, setRole] = useState<"owner" | "mower">("owner");
  const [companyCode, setCompanyCode] = useState("");
  const [resetPhone, setResetPhone] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetStep, setResetStep] = useState<"phone" | "code">("phone");


  useEffect(() => {
    const next = params.get("mode") as Mode | null;
    if (next === "login" || next === "signup" || next === "forgot") setMode(next);
  }, [params]);

  useEffect(() => {
    const r = params.get("role");
    if (r === "owner" || r === "mower") setRole(r);
    const c = params.get("code");
    if (c && /^[A-Za-z]{4}$/.test(c)) setCompanyCode(c.toUpperCase());
    // Crew invite links carry the details the owner already entered so the
    // invite is matched (and claimed) on the phone number they were texted at.
    const n = params.get("name");
    if (n) setFullName(n.slice(0, 80));
    const p = params.get("phone");
    if (p) setPhone(p.slice(0, 30));
  }, [params]);


  useEffect(() => {
    // Only bounce to the dashboard when landing on /auth with no explicit mode.
    if (!loading && user && !params.get("mode")) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate, params]);



  const handleLogin = async () => {
    const parsed = z.object({ email: emailSchema, password: z.string().min(1, "Enter your password") }).safeParse({
      email,
      password,
    });
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: parsed.data.email,
      password: parsed.data.password,
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Welcome back");
    navigate("/dashboard", { replace: true });
  };

  const handleSignup = async () => {
    const parsed = signupSchema.safeParse({ email, password, fullName, phone, company, location, role, companyCode });
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setSubmitting(true);

    if (parsed.data.role === "owner") {
      const { data: available, error: codeError } = await supabase.rpc("is_company_code_available", {
        _code: parsed.data.companyCode,
      });
      if (codeError) {
        setSubmitting(false);
        return toast.error(codeError.message);
      }
      if (!available) {
        setSubmitting(false);
        return toast.error("That company code is already taken — pick another.");
      }
    }

    const { data, error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: parsed.data.fullName,
          phone: parsed.data.phone,
          company: parsed.data.company ?? "",
          location: parsed.data.location,
          role: parsed.data.role,
          company_code: parsed.data.companyCode,
        },
      },
    });

    setSubmitting(false);
    if (error) {
      if (/Database error/i.test(error.message)) {
        return toast.error(
          parsed.data.role === "mower"
            ? "No business found with that company code."
            : "That company code is already taken — pick another.",
        );
      }
      return toast.error(error.message);
    }

    if (!data.session) {
      toast.success("Check your email to confirm your account");
      setMode("login");
      return;
    }
    toast.success("Account created");
    navigate("/dashboard", { replace: true });
  };

  const handleForgotRequest = async () => {
    const parsed = phoneSchema.safeParse(resetPhone);
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("sms-password-reset", {
      body: { action: "request", phone: parsed.data },
    });
    setSubmitting(false);
    if (error) return toast.error((data as { error?: string } | null)?.error ?? "Could not send the code");
    toast.success("If that number is on file, a code is on its way by text");
    setPassword("");
    setResetCode("");
    setResetStep("code");
  };

  const handleForgotVerify = async () => {
    const parsed = z
      .object({
        phone: phoneSchema,
        code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code"),
        password: passwordSchema,
      })
      .safeParse({ phone: resetPhone, code: resetCode, password });
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("sms-password-reset", {
      body: { action: "verify", ...parsed.data },
    });
    setSubmitting(false);
    if (error) return toast.error((data as { error?: string } | null)?.error ?? "Could not reset your password");
    toast.success("Password updated — log in with your new password");
    setPassword("");
    setResetCode("");
    setResetStep("phone");
    setMode("login");
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "login") handleLogin();
    else if (mode === "signup") handleSignup();
    else if (resetStep === "phone") handleForgotRequest();
    else handleForgotVerify();
  };

  const titles: Record<Mode, { title: string; description: string }> = {
    login: { title: "Log in", description: "Welcome back to Mowzi." },
    signup: { title: "Create your account", description: "Tell us a bit about you to get started." },
    forgot: {
      title: "Forgot password",
      description:
        resetStep === "phone"
          ? "We'll text a 6-digit code to the number on your account."
          : "Enter the code we texted you and pick a new password.",
    },
  };


  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden bg-primary text-primary-foreground lg:flex lg:flex-col lg:justify-between lg:p-12">
        <img
          src={stripes}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 w-[420px] opacity-10"
        />
        <img
          src={stripes}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-24 w-[380px] opacity-10"
        />
        <Link to="/" className="relative flex items-center gap-3">
          <img src={logo} alt="Mowzi" className="h-10 w-10 object-contain" />
          <span className="font-display text-2xl font-bold">Mowzi</span>
        </Link>

        <div className="relative">
          <h2 className="max-w-md font-display text-4xl font-bold leading-tight">
            Everything a mowing company needs, in one green dashboard.
          </h2>
          <ul className="mt-8 space-y-3">
            {perks.map((perk) => (
              <li key={perk} className="flex items-center gap-3 text-primary-foreground/90">
                <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-sm text-primary-foreground/70">
          © {new Date().getFullYear()} Mowzi. All rights reserved.
        </p>
      </aside>

      {/* Form panel */}
      <main className="flex min-h-screen items-center justify-center bg-frost px-6 py-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to home
          </Link>

          <Link to="/" className="mb-6 flex items-center justify-center gap-2 lg:hidden">
            <img src={logo} alt="Mowzi" className="h-9 w-9 object-contain" />
            <span className="font-display text-2xl font-bold text-primary">Mowzi</span>
          </Link>

          <Card className="shadow-elevated">
            <CardHeader>
              {mode !== "forgot" && (
                <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)} className="mb-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="login">Log in</TabsTrigger>
                    <TabsTrigger value="signup">Sign up</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
              <CardTitle className="font-display">{titles[mode].title}</CardTitle>
              <CardDescription>{titles[mode].description}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                {mode === "signup" && (
                  <>
                    <div className="space-y-2">
                      <Label>I'm signing up as…</Label>
                      <Tabs value={role} onValueChange={(v) => setRole(v as "owner" | "mower")}>
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="owner">Business owner</TabsTrigger>
                          <TabsTrigger value="mower">Mower / crew</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full name</Label>
                      <Input
                        id="fullName"
                        autoComplete="name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        maxLength={30}
                      />
                    </div>

                    {role === "owner" && (
                      <div className="space-y-2">
                        <Label htmlFor="company">Company name</Label>
                        <Input
                          id="company"
                          autoComplete="organization"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          maxLength={120}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="companyCode">
                        {role === "owner" ? "Create a 4-letter company code" : "Company code"}
                      </Label>
                      <Input
                        id="companyCode"
                        value={companyCode}
                        onChange={(e) =>
                          setCompanyCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))
                        }
                        placeholder="MOWZ"
                        maxLength={4}
                        className="uppercase tracking-[0.35em]"
                      />
                      <p className="text-xs text-muted-foreground">
                        {role === "owner"
                          ? "Your mowers enter this code to join your business."
                          : "Ask your business owner for their 4-letter code."}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        placeholder="City, State"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        maxLength={120}
                      />
                    </div>
                  </>
                )}


                {mode === "forgot" ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="resetPhone">Mobile number</Label>
                      <Input
                        id="resetPhone"
                        type="tel"
                        autoComplete="tel"
                        placeholder="(555) 123-4567"
                        value={resetPhone}
                        onChange={(e) => setResetPhone(e.target.value)}
                        disabled={resetStep === "code"}
                        maxLength={30}
                      />
                      <p className="text-xs text-muted-foreground">
                        Use the mobile number on your Mowzi account.
                      </p>
                    </div>

                    {resetStep === "code" && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="resetCode">6-digit code</Label>
                          <Input
                            id="resetCode"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            maxLength={6}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="newPassword">New password</Label>
                          <Input
                            id="newPassword"
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            maxLength={72}
                          />
                          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
                        </div>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => {
                            setResetStep("phone");
                            setResetCode("");
                          }}
                        >
                          Didn't get the text? Send a new code
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        maxLength={255}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        {mode === "login" && (
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline"
                            onClick={() => setMode("forgot")}
                          >
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete={mode === "login" ? "current-password" : "new-password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          maxLength={72}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <Eye className="h-4 w-4" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                      {mode === "signup" && (
                        <p className="text-xs text-muted-foreground">At least 8 characters.</p>
                      )}
                    </div>
                  </>
                )}

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting
                    ? "Please wait…"
                    : mode === "login"
                      ? "Log in"
                      : mode === "signup"
                        ? "Create account"
                        : resetStep === "phone"
                          ? "Text me a code"
                          : "Reset password"}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-muted-foreground">
                {mode === "forgot" ? (
                  <button className="text-primary hover:underline" onClick={() => setMode("login")}>
                    Back to log in
                  </button>
                ) : mode === "login" ? (
                  <>
                    New to Mowzi?{" "}
                    <button className="text-primary hover:underline" onClick={() => setMode("signup")}>
                      Create an account
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button className="text-primary hover:underline" onClick={() => setMode("login")}>
                      Log in
                    </button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
